// // src/pages/CheckoutSuccess.jsx
// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { fetchOrderBySession } from "../api/payments";
// import { Link, useLocation } from "react-router-dom";

// function useQuery() {
//   return new URLSearchParams(useLocation().search);
// }

// const fmtGBP = (valueMinor, currency = "GBP") =>
//   new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
//     Number(valueMinor || 0) / 100
//   );

// export default function CheckoutSuccess() {
//   const q = useQuery();
//   const sessionId = q.get("session_id");

//   const [loading, setLoading] = useState(Boolean(sessionId));
//   const [order, setOrder] = useState(null);
//   const [error, setError] = useState("");
//   const [attempts, setAttempts] = useState(0);

//   const timeoutRef = useRef(null);
//   const isMountedRef = useRef(true);

//   const maxAttempts = 5;

//   const startFetch = useMemo(
//     () =>
//       async function fetchWithRetry(reset = false) {
//         if (!sessionId) return;

//         if (reset) {
//           setError("");
//           setOrder(null);
//           setAttempts(0);
//           setLoading(true);
//         }

//         try {
//           const data = await fetchOrderBySession(sessionId);
//           if (!isMountedRef.current) return;
//           setOrder(data);
//           setLoading(false);
//         } catch (e) {
//           if (!isMountedRef.current) return;
//           const nextAttempt = (reset ? 0 : attempts) + 1;

//           if (nextAttempt <= maxAttempts) {
//             setAttempts(nextAttempt);
//             const delay = 500 * Math.pow(2, nextAttempt - 1);
//             timeoutRef.current = setTimeout(() => {
//               fetchWithRetry(false);
//             }, delay);
//           } else {
//             setError(
//               e?.response?.data?.error || e?.message || "Order not found yet."
//             );
//             setLoading(false);
//           }
//         }
//       },
//     [sessionId, attempts]
//   );

//   useEffect(() => {
//     isMountedRef.current = true;
//     if (sessionId) startFetch(true);

//     return () => {
//       isMountedRef.current = false;
//       if (timeoutRef.current) clearTimeout(timeoutRef.current);
//     };
//   }, [sessionId, startFetch]);

//   // Background gradient wrapper
//   return (
//     <div className="relative min-h-screen flex items-center justify-center text-white">
//       {/* Gradient overlay */}
//       <div
//         className="absolute inset-0 z-0"
//         style={{
//           background:
//             "linear-gradient(rgba(31, 41, 84, 0.85) 10%, rgba(239, 81, 75, 0.85) 77%)",
//         }}
//       ></div>

//       {/* Content */}
//       <div className="relative z-10 w-full max-w-2xl p-6 text-center bg-white/90 rounded-lg shadow-xl text-primary">
//         {!sessionId && <div>Missing session id.</div>}

//         {loading && (
//           <>
//             <h1 className="text-xl font-semibold">Finalizing your order…</h1>
//             <p className="mt-2 text-gray-600">
//               This can take a moment while we confirm the payment with Stripe.
//             </p>
//             {attempts > 0 && (
//               <p className="mt-2 text-xs text-gray-500">
//                 Attempt {attempts} of {maxAttempts}
//               </p>
//             )}
//           </>
//         )}

//         {error && !loading && !order && (
//           <>
//             <h1 className="text-xl font-semibold text-red-600">
//               We’re still finalizing things
//             </h1>
//             <p className="mt-2 text-gray-700">{error}</p>
//             <button
//               onClick={() => startFetch(true)}
//               className="mt-6 px-5 py-2 rounded bg-[#1f2954] text-white"
//             >
//               Try again
//             </button>
//             <div className="mt-4">
//               <Link to="/used-cars" className="text-sm text-blue-600 underline">
//                 Back to cars
//               </Link>
//             </div>
//           </>
//         )}

//         {order && !loading && (
//           <>
//             <h1 className="text-3xl font-bold text-green-600">
//               Payment successful!
//             </h1>
//             <p className="mt-3 font-medium">Your order ID:</p>
//             <div className="mt-3 text-2xl font-mono">{order.orderId}</div>

//             <div className="mt-6 text-sm text-gray-700 space-y-1">
//               <div>Car ID: {order.carId}</div>
//               <div>
//                 Paid:{" "}
//                 {fmtGBP(
//                   order.amount_total,
//                   (order.currency || "GBP").toUpperCase()
//                 )}
//               </div>
//               {order.includeWarranty && (
//                 <div>Warranty: {order.warrantyLabel}</div>
//               )}
//               {attempts > 0 && (
//                 <div className="text-xs text-gray-500">
//                   Confirmed after {attempts}{" "}
//                   {attempts === 1 ? "retry" : "retries"}.
//                 </div>
//               )}
//             </div>

//             <Link
//               to="/used-cars"
//               className="inline-block mt-8 px-5 py-2 rounded bg-[#1f2954] text-white"
//             >
//               Back to cars
//             </Link>
//           </>
//         )}
//       </div>
//     </div>
//   );
// }

// src/pages/CheckoutSuccess.jsx
import React, { useEffect, useRef, useState } from "react";
import { fetchOrderBySession } from "../api/payments";
import { Link, useLocation } from "react-router-dom";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const fmtCurrency = (valueMinor, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    Number(valueMinor || 0) / 100
  );

export default function CheckoutSuccess() {
  const q = useQuery();
  const sessionId = q.get("session_id");

  const [loading, setLoading] = useState(Boolean(sessionId));
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const abortedRef = useRef(false);

  useEffect(() => {
    abortedRef.current = false;

    async function load() {
      if (!sessionId) return;
      setLoading(true);
      setError("");
      setOrder(null);
      try {
        // Backend webhook should have written the order; the API may block briefly.
        const data = await fetchOrderBySession(sessionId);
        if (!abortedRef.current) setOrder(data);
      } catch (e) {
        if (!abortedRef.current) {
          setError(e?.response?.data?.error || e?.message || "Not ready yet.");
        }
      } finally {
        if (!abortedRef.current) setLoading(false);
      }
    }

    if (sessionId) load();

    return () => {
      abortedRef.current = true;
    };
  }, [sessionId]);

  return (
    <div className="relative min-h-screen flex items-center justify-center text-white">
      {/* Gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(rgba(31, 41, 84, 0.85) 10%, rgba(239, 81, 75, 0.85) 77%)",
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-2xl p-6 text-center bg-white/90 rounded-lg shadow-xl text-primary">
        {!sessionId && <div>Missing session id.</div>}

        {loading && sessionId && (
          <>
            <h1 className="text-xl font-semibold">Processing your payment…</h1>
            <p className="mt-2 text-gray-600">
              Please wait while we confirm the payment with Stripe.
            </p>
          </>
        )}

        {!loading && error && !order && (
          <>
            <h1 className="text-xl font-semibold text-red-600">
              We’re still finalizing things
            </h1>
            <p className="mt-2 text-gray-700">{error}</p>
            <button
              onClick={() => {
                // manual retry
                setError("");
                setOrder(null);
                setLoading(true);
                (async () => {
                  try {
                    const data = await fetchOrderBySession(sessionId);
                    setOrder(data);
                  } catch (e) {
                    setError(
                      e?.response?.data?.error || e?.message || "Not ready yet."
                    );
                  } finally {
                    setLoading(false);
                  }
                })();
              }}
              className="mt-6 px-5 py-2 rounded bg-[#1f2954] text-white"
            >
              Try again
            </button>
            <div className="mt-4">
              <Link to="/used-cars" className="text-sm text-blue-600 underline">
                Back to cars
              </Link>
            </div>
          </>
        )}

        {!loading && order && (
          <>
            <h1 className="text-3xl font-bold text-green-600">
              Payment successful!
            </h1>
            <p className="mt-3 font-medium">Your order ID:</p>
            <div className="mt-3 text-2xl font-mono">{order.orderId}</div>

            <div className="mt-6 text-sm text-gray-700 space-y-1">
              <div>Car ID: {order.carId}</div>
              <div>
                Paid:{" "}
                {fmtCurrency(
                  order.amount_total,
                  (order.currency || "GBP").toUpperCase()
                )}
              </div>
              {order.includeWarranty && (
                <div>Warranty: {order.warrantyLabel}</div>
              )}
              {order.receiptUrl && (
                <div className="mt-2">
                  <a
                    href={order.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline"
                  >
                    View receipt
                  </a>
                </div>
              )}
            </div>

            <Link
              to="/used-cars"
              className="inline-block mt-8 px-5 py-2 rounded bg-[#1f2954] text-white"
            >
              Back to cars
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
